var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CircularProgress, Grid, TableCell, TableRow, Typography, Backdrop, } from "@mui/material";
import React, { useEffect, useState } from "react";
import LsdItem from "../lsd/LsdItem";
const ResultTableRows = ({ sortConfig, trialId, categoryId, checkedDataTypeIds, // From ResultDataTypeCheckboxList >> ResultHeader >> Results >> ResultTableContainer
onLoaded, results, }) => {
    const [distinctRowVariety, setDistinctRowVariety] = useState([]);
    const [columns, setColumns] = useState([]);
    const [sortedColumns, setSortedColumns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [countDown, setCountDown] = useState(0);
    const totalDuration = 3000;
    const intervalDuration = 100; // Interval duration in milliseconds
    //================================================================================================
    //                                      useEffect Function
    //            Getting the new ResultTableRow from the api and setting the state
    //================================================================================================
    useEffect(() => {
        const getTableResults = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                let result = results;
                // Add this safety check
                if (!result || result.length === 0) {
                    console.log("No results to process");
                    setDistinctRowVariety([]);
                    setColumns([]);
                    return;
                }
                //console.log("Processing results:", result.length);
                if (result.length > 0 &&
                    (sortConfig.selectedColumnID === null ||
                        sortConfig.selectedColumnID === undefined ||
                        sortConfig.selectedColumnID === "")) {
                    //get the first columnID in the result set as this can be controlled by the db query
                    sortConfig.selectedColumnID = result[0].columnID;
                }
                else {
                    // if the selectedColumnID is not in the result set, set it to the first one
                    const columnIDs = result.map((item) => item.columnID);
                    if (!columnIDs.includes(sortConfig.selectedColumnID)) {
                        sortConfig.selectedColumnID = columnIDs[0];
                    }
                }
                // this has no columnID, so need to handle it separately
                const sortByVarietyName = sortConfig.selectedColumnID === "varietyName";
                const rowsVarieties = result
                    //filter by selectedColumnID
                    .filter((item) => item.columnID === sortConfig.selectedColumnID || sortByVarietyName)
                    // sort by PredMeanValue desc
                    .sort((a, b) => {
                    if (sortByVarietyName) {
                        if (sortConfig.direction === "asc") {
                            return a.varietyName.localeCompare(b.varietyName);
                        }
                        return b.varietyName.localeCompare(a.varietyName);
                    }
                    return b.predMeanValue - a.predMeanValue;
                })
                    .map((item) => ({
                    varietyId: item.varietyID,
                    varietyName: item.varietyName,
                    varietyTestLine: "varietyTestLine" in item ? item.varietyTestLine : null,
                }))
                    .filter((value, index, self) => index ===
                    self.findIndex((t) => t.varietyId === value.varietyId &&
                        t.varietyName === value.varietyName));
                setDistinctRowVariety(rowsVarieties);
                // Filtering for the columns "AFTER" the variety rows.
                // if the checkedDataTypeIds is empty then don't filter the result
                if (checkedDataTypeIds.length > 0) {
                    result = result.filter((item) => checkedDataTypeIds.includes(item.dataResultTypeId));
                }
                //distinct columns based on ColumnID
                const cols = result
                    .map((item) => ({
                    columnId: item.columnID,
                    columnName: item.columnName,
                    displayName: item.columnName.replace(/([a-z])([A-Z])/g, "$1 $2"),
                    originalDisplayIndex: item.columnDisplayIndex,
                    sortedDisplayIndex: 0,
                    dataResultUnit: item.dataResultUnit,
                }))
                    .filter((value, index, self) => index ===
                    self.findIndex((t) => t.columnId === value.columnId &&
                        t.columnName === value.columnName))
                    .sort((a, b) => a.columnName.localeCompare(b.columnName));
                setColumns(cols);
                //console.log("Columns Set",  cols );
            }
            catch (error) {
                console.error("Error fetching trial results", error);
            }
        });
        getTableResults();
    }, [sortConfig, results, checkedDataTypeIds, categoryId, trialId, onLoaded]);
    useEffect(() => {
        const sortColumns = () => {
            const updatedColumns = columns.map((column, index) => {
                if (!column.sortedDisplayIndex) {
                    column.sortedDisplayIndex = 0;
                }
                column.sortedDisplayIndex = column.originalDisplayIndex * 10;
                return column;
            });
            try {
                if (!updatedColumns) {
                    console.log("No columns found");
                    return;
                }
                const varietyNameColumn = updatedColumns.find((column) => column.columnName === "VarietyName");
                if (varietyNameColumn) {
                    varietyNameColumn.sortedDisplayIndex = 0;
                }
                //updatedColumns.find((column) => column.columnId === sortConfig.selectedColumnID)!.SortedDisplayIndex = 5;
                const selectedColumn = updatedColumns.find((column) => column.columnId === sortConfig.selectedColumnID);
                if (selectedColumn) {
                    selectedColumn.sortedDisplayIndex = 5;
                }
                updatedColumns.sort((a, b) => a.sortedDisplayIndex - b.sortedDisplayIndex);
            }
            catch (error) {
                console.log("error", error);
            }
            // console.log('sortColumns', { updatedColumns });
            setSortedColumns(updatedColumns);
        };
        sortColumns();
    }, [columns, sortConfig]);
    //================================================================================================
    // UseEffect on sortDirection and selectedColumnIndex changes
    // This will update the setDistinctRowVariety to sort the PredMeanValue
    //    based on the selectedColumnIndex and direction
    //================================================================================================
    useEffect(() => {
        // Only run this if we actually have varieties to sort
        if (distinctRowVariety.length === 0)
            return;
        // Create a new array to avoid mutating state
        const sortedVarieties = [...distinctRowVariety].sort((a, b) => {
            const cellA = results.find((cell) => cell.varietyID === a.varietyId &&
                cell.columnID === sortConfig.selectedColumnID);
            const cellB = results.find((cell) => cell.varietyID === b.varietyId &&
                cell.columnID === sortConfig.selectedColumnID);
            if (cellA && cellB) {
                if (sortConfig.direction === "asc") {
                    return cellA.predMeanValue - cellB.predMeanValue;
                }
                else {
                    return cellB.predMeanValue - cellA.predMeanValue;
                }
            }
            return 0;
        });
        // Compare if arrays are actually different before updating state
        const hasChanged = JSON.stringify(sortedVarieties) !== JSON.stringify(distinctRowVariety);
        if (hasChanged) {
            setDistinctRowVariety(sortedVarieties);
        }
    }, [
        results,
        sortConfig.direction,
        sortConfig.selectedColumnID,
        distinctRowVariety,
    ]);
    const columnWidth = 150; // `${100 / distinctColumns.length}%`; // Calculate equal width for each column
    // For slow connections, set a loading time of 6 seconds
    useEffect(() => {
        const startTime = Date.now();
        const interval = setInterval(() => {
            const elapsedTime = Date.now() - startTime;
            const percentage = Math.min((elapsedTime / totalDuration) * 100, 100); // Calculate percentage
            setCountDown(percentage);
            if (elapsedTime >= totalDuration) {
                clearInterval(interval);
                setLoading(false); // Stop loading when the timer finishes
                onLoaded(); // Call the onLoaded callback
            }
        }, intervalDuration);
        return () => clearInterval(interval); // Cleanup interval on unmount
    }, [onLoaded]);
    //================================================================================================
    // Render Backdrop for Loading
    //================================================================================================
    if (loading) {
        return (React.createElement(TableRow, { sx: {
                height: "2em",
                backgroundColor: "#ffffff",
                textAlign: "center",
            } },
            React.createElement(TableCell, { colSpan: sortedColumns.length || 1, align: "center" },
                React.createElement(Backdrop, { open: loading, sx: {
                        color: "#fff",
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                    } },
                    React.createElement(CircularProgress, { size: 80, variant: "determinate", value: countDown }),
                    React.createElement(Typography, { sx: {
                            marginTop: 2,
                            fontSize: "1.2em",
                            color: "#fff",
                            marginLeft: 2,
                        } },
                        Math.round(countDown),
                        "% - Loading your trial results...")))));
    }
    return (React.createElement(React.Fragment, null, distinctRowVariety.length === 0 ? (React.createElement(TableRow, { sx: {
            height: "2em",
            backgroundColor: "#ffffff",
            textAlign: "center",
        } },
        React.createElement(TableCell, { colSpan: sortedColumns.length || 1, align: "center" },
            React.createElement(Typography, null, "No data available")))) : (distinctRowVariety.map((variety, index) => (React.createElement(TableRow, { key: index, sx: {
            // alternating row color
            backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff",
            fontSize: "0.9em !important",
        } },
        React.createElement(TableCell, { sx: {
                width: columnWidth,
                fontSize: "0.9em !important",
                padding: 0,
                textAlign: "center",
                backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff",
            }, "data-column": "varietyName" },
            React.createElement(Typography, { sx: { fontSize: "0.9em !important" } }, variety.varietyName)),
        sortedColumns.map((column, colIndex) => {
            var _a;
            const cell = results.find((cell) => {
                return (cell.varietyID === variety.varietyId &&
                    cell.columnID === column.columnId);
            });
            const lsdMaxGroupNumber = (cell === null || cell === void 0 ? void 0 : cell.maxGroupNumber) || 4;
            return (React.createElement(TableCell, { key: colIndex, sx: {
                    width: columnWidth,
                    textAlign: "center",
                    fontSize: "0.9em",
                    padding: 0,
                }, "data-column": column.columnName },
                React.createElement(Grid, { container: true },
                    React.createElement(Grid, { item: true, xs: 6 }, cell ? Number(cell.predMeanValue).toFixed(1) : "0.0"),
                    React.createElement(Grid, { item: true, xs: 6 }, column.columnId === sortedColumns[0].columnId && (React.createElement(LsdItem, { lsdProps: typeof (cell === null || cell === void 0 ? void 0 : cell.columnLsd) === "string"
                            ? cell === null || cell === void 0 ? void 0 : cell.columnLsd.split(",").map(Number)
                            : (_a = cell === null || cell === void 0 ? void 0 : cell.columnLsd) !== null && _a !== void 0 ? _a : [], maxGroupNumber: lsdMaxGroupNumber + 2 }))))));
        })))))));
};
export default ResultTableRows;
